import { buildPlayerEmbedUrl, createEmbedChannelId, normalizeEmbedOrigin } from './bootstrap';
import { createHostEmbedBridge, type SecureEmbedBridge } from './bridge';
import {
  createHostMessage,
  embedProtocolVersion,
  type EmbedHostCommandType,
  type EmbedHostMessage,
  type EmbedHostPayloadMap,
  type EmbedPlayerEventType,
  type EmbedPlayerMessage,
  type EmbedPlayerPayloadMap,
} from './protocol';

export interface EmbedConfiguration {
  readonly playerOrigin: string;
  readonly title?: string;
  readonly readyTimeoutMs?: number;
  readonly commandTimeoutMs?: number;
}

export type EmbedControlCommandType = Exclude<EmbedHostCommandType, 'HOST_HELLO'>;

export interface RetroArcadeEmbedClient {
  readonly ready: Promise<EmbedPlayerPayloadMap['PLAYER_READY']>;
  start(): void;
  loadGame(game: string): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  restart(): Promise<void>;
  setVolume(volume: number): Promise<void>;
  requestFullscreen(): Promise<void>;
  on<TType extends EmbedPlayerEventType>(
    type: TType,
    listener: (payload: EmbedPlayerPayloadMap[TType]) => void,
  ): () => void;
  destroy(): void;
}

export interface RetroArcadeEmbedHandle extends Omit<RetroArcadeEmbedClient, 'start' | 'destroy'> {
  readonly iframe: HTMLIFrameElement;
  destroy(): void;
}

interface PendingCommand {
  readonly command: EmbedControlCommandType;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
  readonly timeoutId: ReturnType<typeof setTimeout>;
}

interface EmbedClientOptions {
  readonly channelId: string;
  readonly readyTimeoutMs?: number;
  readonly commandTimeoutMs?: number;
}

const defaultReadyTimeoutMs = 10_000;
const defaultCommandTimeoutMs = 10_000;

function timeoutValue(value: number | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  if (!Number.isFinite(value) || value < 250 || value > 60_000) {
    throw new RangeError('Embed timeout must be between 250 and 60000 milliseconds.');
  }
  return value;
}

export function createRetroArcadeEmbedClient(
  bridge: SecureEmbedBridge<EmbedPlayerMessage, EmbedHostMessage>,
  options: EmbedClientOptions,
): RetroArcadeEmbedClient {
  const readyTimeoutMs = timeoutValue(options.readyTimeoutMs, defaultReadyTimeoutMs);
  const commandTimeoutMs = timeoutValue(options.commandTimeoutMs, defaultCommandTimeoutMs);
  const pending = new Map<string, PendingCommand>();
  const listeners = new Map<EmbedPlayerEventType, Set<(payload: unknown) => void>>();
  let sequence = 0;
  let started = false;
  let destroyed = false;
  let readySettled = false;
  let supportedCommands = new Set<EmbedHostCommandType>();
  let readyTimer: ReturnType<typeof setTimeout> | null = null;
  let resolveReady!: (payload: EmbedPlayerPayloadMap['PLAYER_READY']) => void;
  let rejectReady!: (error: Error) => void;

  const ready = new Promise<EmbedPlayerPayloadMap['PLAYER_READY']>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  const nextMessageId = () => {
    sequence += 1;
    return `host-${sequence}`;
  };

  const emit = (type: EmbedPlayerEventType, payload: unknown) => {
    for (const listener of listeners.get(type) ?? []) listener(payload);
  };

  const unsubscribe = bridge.subscribe((message) => {
    emit(message.type, message.payload);

    if (message.type === 'PLAYER_READY') {
      if (readySettled) return;
      readySettled = true;
      supportedCommands = new Set(message.payload.supportedCommands);
      if (readyTimer !== null) clearTimeout(readyTimer);
      readyTimer = null;
      resolveReady(message.payload);
      return;
    }

    if (message.type === 'COMMAND_RESULT') {
      const command = pending.get(message.payload.requestId);
      if (!command || command.command !== message.payload.command) return;

      clearTimeout(command.timeoutId);
      pending.delete(message.payload.requestId);
      if (message.payload.ok) {
        command.resolve();
      } else {
        command.reject(
          new Error(
            message.payload.code ?? `Embed command ${message.payload.command} was rejected.`,
          ),
        );
      }
      return;
    }

    if (message.type === 'PLAYER_ERROR' && message.payload.requestId) {
      const command = pending.get(message.payload.requestId);
      if (!command) return;
      clearTimeout(command.timeoutId);
      pending.delete(message.payload.requestId);
      command.reject(new Error(`${message.payload.code}: ${message.payload.message}`));
    }
  });

  async function sendCommand<TType extends EmbedControlCommandType>(
    type: TType,
    payload: EmbedHostPayloadMap[TType],
  ): Promise<void> {
    await ready;
    if (destroyed) throw new Error('Retro Arcade embed client has been destroyed.');
    if (!supportedCommands.has(type)) {
      throw new Error(`Player does not support embed command ${type}.`);
    }

    const messageId = nextMessageId();
    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pending.delete(messageId);
        reject(new Error(`Embed command ${type} timed out.`));
      }, commandTimeoutMs);

      pending.set(messageId, { command: type, resolve, reject, timeoutId });
      try {
        bridge.send(createHostMessage(options.channelId, messageId, type, payload));
      } catch (error) {
        clearTimeout(timeoutId);
        pending.delete(messageId);
        reject(error instanceof Error ? error : new Error('Embed command could not be sent.'));
      }
    });
  }

  return {
    ready,
    start() {
      if (destroyed) throw new Error('Retro Arcade embed client has been destroyed.');
      if (started) return;
      started = true;
      readyTimer = setTimeout(() => {
        if (readySettled) return;
        readySettled = true;
        rejectReady(new Error('Retro Arcade Player did not complete the embed handshake.'));
      }, readyTimeoutMs);

      try {
        bridge.send(
          createHostMessage(options.channelId, nextMessageId(), 'HOST_HELLO', {
            requestedVersion: embedProtocolVersion,
          }),
        );
      } catch (error) {
        if (readyTimer !== null) clearTimeout(readyTimer);
        readyTimer = null;
        readySettled = true;
        rejectReady(error instanceof Error ? error : new Error('Embed handshake could not start.'));
      }
    },
    loadGame(game) {
      const normalized = game.trim();
      if (!normalized || normalized.length > 200) {
        return Promise.reject(
          new RangeError('Game identifier must be between 1 and 200 characters.'),
        );
      }
      return sendCommand('LOAD_GAME', { game: normalized });
    },
    pause() {
      return sendCommand('PAUSE', null);
    },
    resume() {
      return sendCommand('RESUME', null);
    },
    restart() {
      return sendCommand('RESTART', null);
    },
    setVolume(volume) {
      if (!Number.isFinite(volume) || volume < 0 || volume > 1) {
        return Promise.reject(new RangeError('Volume must be between 0 and 1.'));
      }
      return sendCommand('SET_VOLUME', { volume });
    },
    requestFullscreen() {
      return sendCommand('REQUEST_FULLSCREEN', null);
    },
    on(type, listener) {
      const wrapped = (payload: unknown) => listener(payload as EmbedPlayerPayloadMap[typeof type]);
      let group = listeners.get(type);
      if (!group) {
        group = new Set();
        listeners.set(type, group);
      }
      group.add(wrapped);
      return () => {
        group?.delete(wrapped);
        if (group?.size === 0) listeners.delete(type);
      };
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      unsubscribe();
      bridge.destroy();
      listeners.clear();

      if (readyTimer !== null) clearTimeout(readyTimer);
      readyTimer = null;
      if (!readySettled) {
        readySettled = true;
        rejectReady(new Error('Retro Arcade embed client was destroyed before READY.'));
      }

      for (const command of pending.values()) {
        clearTimeout(command.timeoutId);
        command.reject(new Error('Retro Arcade embed client was destroyed.'));
      }
      pending.clear();
    },
  };
}

export function assertEmbedOriginIsolation(
  playerOriginValue: string,
  hostOriginValue: string,
): { playerOrigin: string; hostOrigin: string } {
  const playerOrigin = normalizeEmbedOrigin(playerOriginValue);
  const hostOrigin = normalizeEmbedOrigin(hostOriginValue);
  if (playerOrigin === hostOrigin) {
    throw new Error(
      'Retro Arcade Player must use a different origin from the host to preserve iframe isolation.',
    );
  }
  return { playerOrigin, hostOrigin };
}

export function mountRetroArcade(
  container: HTMLElement,
  configuration: EmbedConfiguration,
): RetroArcadeEmbedHandle {
  const { playerOrigin, hostOrigin } = assertEmbedOriginIsolation(
    configuration.playerOrigin,
    window.location.origin,
  );

  const channelId = createEmbedChannelId();
  const iframe = document.createElement('iframe');
  iframe.title = configuration.title?.trim() || 'Retro Arcade Player';
  iframe.className = 'retro-arcade-embed-frame';
  iframe.referrerPolicy = 'no-referrer';
  iframe.allow = 'fullscreen; gamepad';
  iframe.allowFullscreen = true;
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-downloads allow-forms allow-pointer-lock',
  );
  iframe.style.display = 'block';
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  iframe.style.border = '0';
  iframe.src = buildPlayerEmbedUrl(`${playerOrigin}/`, hostOrigin, channelId);

  let client: RetroArcadeEmbedClient | null = null;
  let destroyed = false;
  let resolveReady!: (payload: EmbedPlayerPayloadMap['PLAYER_READY']) => void;
  let rejectReady!: (error: Error) => void;
  const ready = new Promise<EmbedPlayerPayloadMap['PLAYER_READY']>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  const preListeners = new Map<EmbedPlayerEventType, Set<(payload: unknown) => void>>();
  const clientUnsubscribers: Array<() => void> = [];

  const onLoad = () => {
    if (destroyed || client) return;
    const playerWindow = iframe.contentWindow;
    if (!playerWindow) {
      rejectReady(new Error('Retro Arcade Player iframe has no contentWindow.'));
      return;
    }

    const bridge = createHostEmbedBridge({
      eventTarget: window,
      playerWindow,
      expectedPlayerSource: playerWindow,
      playerOrigin,
      channelId,
    });
    client = createRetroArcadeEmbedClient(bridge, {
      channelId,
      ...(configuration.readyTimeoutMs === undefined
        ? {}
        : { readyTimeoutMs: configuration.readyTimeoutMs }),
      ...(configuration.commandTimeoutMs === undefined
        ? {}
        : { commandTimeoutMs: configuration.commandTimeoutMs }),
    });

    for (const type of [
      'PLAYER_READY',
      'GAME_CHANGED',
      'RUNTIME_STATE',
      'COMMAND_RESULT',
      'PLAYER_ERROR',
    ] as const) {
      clientUnsubscribers.push(
        client.on(type, (payload) => {
          for (const listener of preListeners.get(type) ?? []) listener(payload);
        }),
      );
    }

    void client.ready.then(resolveReady, rejectReady);
    client.start();
  };

  iframe.addEventListener('load', onLoad, { once: true });
  container.appendChild(iframe);

  const withClient = async (action: (activeClient: RetroArcadeEmbedClient) => Promise<void>) => {
    await ready;
    if (!client || destroyed) throw new Error('Retro Arcade embed is not active.');
    return action(client);
  };

  return {
    iframe,
    ready,
    loadGame(game) {
      return withClient((activeClient) => activeClient.loadGame(game));
    },
    pause() {
      return withClient((activeClient) => activeClient.pause());
    },
    resume() {
      return withClient((activeClient) => activeClient.resume());
    },
    restart() {
      return withClient((activeClient) => activeClient.restart());
    },
    setVolume(volume) {
      return withClient((activeClient) => activeClient.setVolume(volume));
    },
    requestFullscreen() {
      if (destroyed) {
        return Promise.reject(new Error('Retro Arcade embed is not active.'));
      }
      if (typeof iframe.requestFullscreen !== 'function') {
        return Promise.reject(new Error('Fullscreen API is unavailable for the Player iframe.'));
      }
      return iframe.requestFullscreen();
    },
    on(type, listener) {
      const wrapped = (payload: unknown) => listener(payload as EmbedPlayerPayloadMap[typeof type]);
      let group = preListeners.get(type);
      if (!group) {
        group = new Set();
        preListeners.set(type, group);
      }
      group.add(wrapped);
      return () => {
        group?.delete(wrapped);
        if (group?.size === 0) preListeners.delete(type);
      };
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      iframe.removeEventListener('load', onLoad);
      for (const unsubscribeClient of clientUnsubscribers) unsubscribeClient();
      clientUnsubscribers.length = 0;
      client?.destroy();
      client = null;
      preListeners.clear();
      iframe.remove();
      rejectReady(new Error('Retro Arcade embed was destroyed.'));
    },
  };
}
